import { MapPin, Phone, Star, Clock, Navigation, MessageCircle } from "lucide-react";
import { CLINIC, buildWhatsAppLink } from "../lib/clinic";

const CARDS = [
  {
    icon: MapPin,
    title: "Address",
    lines: [CLINIC.address.line1, CLINIC.address.line2, `${CLINIC.address.state} ${CLINIC.address.pin}`],
  },
  {
    icon: Phone,
    title: "Phone",
    lines: [CLINIC.phone],
    href: `tel:${CLINIC.phoneRaw}`,
  },
  {
    icon: Star,
    title: "Google Rating",
    lines: [`${CLINIC.rating} ★ · ${CLINIC.reviewCount} Reviews`],
  },
  {
    icon: Clock,
    title: "Working Hours",
    lines: [CLINIC.hours.weekday, CLINIC.hours.sunday],
  },
];

export const Contact = () => {
  const waLink = buildWhatsAppLink(
    `Hello Abhayam Dental Clinic, I have an enquiry.`
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center reveal max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#4FC3F7]/15 text-[#0288d1] text-xs font-semibold tracking-wide uppercase">
            Contact
          </div>
          <h2 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937] leading-[1.05]">
            Visit us in <span className="text-[#009688]">Manjeri</span>.
          </h2>
          <p className="mt-5 text-lg text-[#1F2937]/65">
            We&apos;re easy to reach — right in the heart of Vellarangal, Manjeri.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {CARDS.map((c, i) => {
            const Content = (
              <>
                <div className="w-11 h-11 rounded-xl bg-[#009688]/10 grid place-items-center mb-4 group-hover:bg-[#009688] transition-colors">
                  <c.icon className="w-5 h-5 text-[#009688] group-hover:text-white transition-colors" />
                </div>
                <div className="text-xs uppercase tracking-wider text-[#1F2937]/50 font-semibold">
                  {c.title}
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {c.lines.map((l, j) => (
                    <div
                      key={j}
                      className="text-[14.5px] text-[#1F2937] font-medium leading-snug"
                    >
                      {l}
                    </div>
                  ))}
                </div>
              </>
            );
            const cls =
              "group bg-white rounded-2xl border border-[#009688]/12 p-5 sm:p-6 hover:border-[#009688]/40 hover:-translate-y-0.5 transition-all shadow-[0_4px_20px_rgb(0,0,0,0.02)]";
            return c.href ? (
              <a
                key={c.title}
                href={c.href}
                data-testid={`contact-card-${i}`}
                className={cls}
              >
                {Content}
              </a>
            ) : (
              <div
                key={c.title}
                data-testid={`contact-card-${i}`}
                className={cls}
              >
                {Content}
              </div>
            );
          })}
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-3 rounded-3xl overflow-hidden border border-[#009688]/12 shadow-[0_8px_30px_rgba(0,0,0,0.05)] min-h-[380px]">
            <iframe
              data-testid="google-map"
              title="Abhayam Dental Clinic Location"
              src={CLINIC.mapEmbed}
              loading="lazy"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 380 }}
              allowFullScreen=""
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="lg:col-span-2 rounded-3xl bg-gradient-to-br from-[#009688] to-[#00796b] text-white p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-80 font-semibold">
                Ready to visit?
              </div>
              <h3 className="mt-3 text-3xl font-semibold tracking-tight leading-tight">
                Get in touch with us today.
              </h3>
              <p className="mt-3 text-sm opacity-85 leading-relaxed">
                Call, WhatsApp or drop by our clinic — our friendly team is
                ready to help.
              </p>
            </div>

            <div className="mt-6 grid gap-3">
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                data-testid="contact-call-btn"
                className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white text-[#009688] font-semibold hover:bg-[#F7FAFC] transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Phone className="w-5 h-5" />
                  Call Now
                </span>
                <span className="text-sm opacity-70">{CLINIC.phone}</span>
              </a>
              <a
                href={CLINIC.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-btn"
                className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <Navigation className="w-5 h-5" />
                  Get Directions
                </span>
                <span className="text-sm opacity-80">→</span>
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-[#25D366] text-white font-semibold hover:bg-[#1fb356] transition-colors"
              >
                <span className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </span>
                <span className="text-sm opacity-90">Chat now →</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
